import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

export const CartContext = createContext({
  cartItems: [],
  wishlist: [],
});

const readStorage = (key, fallback) => {
  if (typeof window === "undefined") return fallback;
  try {
    const value = window.localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() =>
    readStorage("awmeadow-cart", [])
  );
  const [wishlist, setWishlist] = useState(() =>
    readStorage("awmeadow-wishlist", [])
  );

  useEffect(() => {
    try {
      window.localStorage.setItem("awmeadow-cart", JSON.stringify(cartItems));
    } catch {
      // Storage can be unavailable in strict privacy modes.
    }
  }, [cartItems]);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        "awmeadow-wishlist",
        JSON.stringify(wishlist)
      );
    } catch {
      // Wishlist remains available for the current session.
    }
  }, [wishlist]);

  const addToCart = useCallback((productId, quantity = 1, size, color) => {
    const safeQuantity = Math.max(1, Number(quantity) || 1);
    setCartItems((items) => {
      const existing = items.find(
        (item) =>
          item.id === productId &&
          item.size === size &&
          item.color === color
      );
      if (!existing) {
        return [
          ...items,
          { id: productId, size, color, quantity: safeQuantity },
        ];
      }
      return items.map((item) =>
        item === existing
          ? { ...item, quantity: Math.min(10, item.quantity + safeQuantity) }
          : item
      );
    });
  }, []);

  const removeFromCart = useCallback((productId, size, color) => {
    setCartItems((items) =>
      items.filter(
        (item) =>
          !(
            item.id === productId &&
            item.size === size &&
            item.color === color
          )
      )
    );
  }, []);

  const updateQuantity = useCallback((productId, size, color, quantity) => {
    const safeQuantity = Math.min(10, Math.max(1, Number(quantity) || 1));
    setCartItems((items) =>
      items.map((item) =>
        item.id === productId &&
        item.size === size &&
        item.color === color
          ? { ...item, quantity: safeQuantity }
          : item
      )
    );
  }, []);

  const clearCart = useCallback(() => setCartItems([]), []);

  const addToWishlist = useCallback((productId) => {
    setWishlist((items) =>
      items.includes(productId) ? items : [...items, productId]
    );
  }, []);

  const removeFromWishlist = useCallback((productId) => {
    setWishlist((items) => items.filter((id) => id !== productId));
  }, []);

  const toggleWishlist = useCallback((productId) => {
    setWishlist((items) =>
      items.includes(productId)
        ? items.filter((id) => id !== productId)
        : [...items, productId]
    );
  }, []);

  const value = useMemo(
    () => ({
      cartItems,
      wishlist,
      cartCount: cartItems.reduce((total, item) => total + item.quantity, 0),
      wishlistCount: wishlist.length,
      addToCart,
      addCart: addToCart,
      removeFromCart,
      removeCart: removeFromCart,
      updateQuantity,
      clearCart,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      isWishlisted: (productId) => wishlist.includes(productId),
    }),
    [
      addToCart,
      addToWishlist,
      cartItems,
      clearCart,
      removeFromCart,
      removeFromWishlist,
      toggleWishlist,
      updateQuantity,
      wishlist,
    ]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
